import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useFamily } from '@/hooks/useFamily';
import { FamilyHeader } from '@/components/FamilyHeader';
import { InvitationModalDrawer } from '@/components/InvitationModalDrawer';
import { DesktopDashboard } from '@/components/dashboard/DesktopDashboard';
import { MobileDashboard } from '@/components/mobile/MobileDashboard';

import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { useFamilySubscription } from '@/hooks/useFamilySubscription';
import { useOptimizedFamily } from '@/hooks/useOptimizedFamily';
import { useToast } from '@/hooks/use-toast';
import { SubscriptionSuccess } from '@/components/SubscriptionSuccess';
import { SubscriptionCancelled } from '@/components/SubscriptionCancelled';
import { EmailConfirmationBanner } from '@/components/EmailConfirmationBanner';
import { DemoBanner } from '@/components/DemoBanner';
import { useDemoMode } from '@/hooks/useDemoMode';

const Dashboard = () => {
  // CRITICAL: All hooks must be called unconditionally at the top
  const { user, signOut, loading } = useAuth();
  const { profile, family: familyData, familyMembers, loading: familyLoading, createFamily, refreshData } = useFamily();
  const isMobile = useIsMobile();
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { family } = useOptimizedFamily();
  const { refreshSubscriptionStatus } = useFamilySubscription(family?.id);
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const [showSubscriptionCancelled, setShowSubscriptionCancelled] = useState(false);
  const { isDemoActive } = useDemoMode(family?.id);
  
  // Use ref to track if we've already redirected in this component lifecycle
  const hasRedirectedRef = useRef(false);
  
  // Gestion des paramètres de retour de paiement
  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    
    if (subscriptionStatus === 'success') {
      setShowSubscriptionSuccess(true);
      // Force refresh du statut de souscription
      refreshSubscriptionStatus();
      // Nettoyer l'URL
      setSearchParams({});
    } else if (subscriptionStatus === 'cancelled') {
      setShowSubscriptionCancelled(true);
      // Nettoyer l'URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, refreshSubscriptionStatus]);

  // Auto-logout si problème d'accès
  useEffect(() => {
    const handleLogout = async () => {
      await signOut();
    };
    
    // Auto-logout après 2 secondes si pas de contenu
    if (user && !familyLoading && !profile) {
      const timeout = setTimeout(handleLogout, 2000);
      return () => clearTimeout(timeout);
    }
  }, [user, familyLoading, profile]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Reset redirect flag when family is detected
  useEffect(() => {
    if (family) {
      hasRedirectedRef.current = false;
      sessionStorage.removeItem('hasRedirectedToOnboarding');
    }
  }, [family]);

  // Redirect to onboarding if no family (only once per component lifecycle)
  useEffect(() => {
    console.log('🔍 Dashboard onboarding check:', { 
      familyLoading, 
      hasProfile: !!profile, 
      hasFamily: !!family,
      hasRedirected: hasRedirectedRef.current
    });
    
    if (!familyLoading && profile && !family && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;
      localStorage.setItem('onboardingMode', '1');
      sessionStorage.setItem('isNewUserSignup', 'true');
      console.log('🔄 Dashboard: No family detected, redirecting to onboarding');
      navigate('/new-user-onboarding', { replace: true });
    }
  }, [familyLoading, profile, family, navigate]);
  
  // Bouton d'urgence si pas d'accès au contenu
  if (!familyLoading && !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
        <h2 className="text-xl font-bold">Problème d'accès</h2>
        <p className="text-muted-foreground text-center">
          Nous n'arrivons pas à charger votre profil. Essayez de vous reconnecter.
        </p>
        <Button 
          onClick={async () => {
            await signOut();
          }} 
          className="flex items-center gap-2"
        >
          <LogOut className="h-4 w-4" />
          Se déconnecter
        </Button>
      </div>
    );
  }

  // Mobile dashboard now uses the same structure with FamilyHeader

  // For desktop, only render dashboard content on dashboard routes
  if (!isMobile && !(window.location.pathname === '/dashboard' || window.location.pathname === '/')) {
    // This means we're on desktop but not on dashboard route, return children as-is
    return null;
  }

  if (familyLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto border-4 border-gray-200 border-t-primary rounded-full animate-spin"></div>
          <p className="text-black text-lg font-medium">Chargement des données du clan</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Demo Banner - shown when demo content is active */}
      {isDemoActive && family?.id && (
        <DemoBanner familyId={family.id} />
      )}
      
      {/* Email Confirmation Banner */}
      <EmailConfirmationBanner />
      
      {/* Mobile: Header de famille avec photo et accueil */}
      {profile && isMobile && (
        <FamilyHeader 
          profile={profile}
          family={familyData}
          familyMembers={familyMembers}
          loading={familyLoading}
        />
      )}
      
      {/* Desktop: Full dashboard layout */}
      {!isMobile && (
        <DesktopDashboard />
      )}
      
      {/* Mobile: Dashboard content */}
      {isMobile && profile && (
        <MobileDashboard />
      )}
      
      {/* Invitation Modal */}
      {familyData && (
        <InvitationModalDrawer
          open={isInvitationModalOpen}
          onOpenChange={setIsInvitationModalOpen}
          familyId={familyData.id}
        />
      )}

      {/* Modales de souscription */}
      {showSubscriptionSuccess && (
        <SubscriptionSuccess 
          onContinue={() => setShowSubscriptionSuccess(false)}
        />
      )}
      
      {showSubscriptionCancelled && (
        <SubscriptionCancelled 
          onContinue={() => setShowSubscriptionCancelled(false)}
          onRetry={() => {
            setShowSubscriptionCancelled(false);
            navigate('/upgrade');
          }}
        />
      )}
    </>
  );
};

export default Dashboard;